import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import {
  LayoutDashboard, Users, UserPlus, Settings, Calendar, Wrench, HardHat,
  Facebook, BarChart3, MessageSquare, Globe, UserCheck, UserCog, Search,
  ArrowRight, Command, Loader2, Mail,
} from 'lucide-react';

const PAGES = [
  { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, keywords: 'home overview stats' },
  // Customers
  { label: 'All Customers', path: '/customers', icon: Users, keywords: 'clients list' },
  { label: 'Add Customer', path: '/customers/new', icon: UserPlus, keywords: 'new create client' },
  { label: 'Postcode Checker', path: '/postcode-checker', icon: Globe, keywords: 'postcode area check dno' },
  { label: 'Archive', path: '/archive', icon: Globe, keywords: 'archived customers' },
  // Leads
  { label: 'Website Leads', path: '/website-leads', icon: Globe, keywords: 'wix forms enquiry' },
  { label: 'Meta Leads', path: '/meta-leads', icon: Facebook, keywords: 'facebook ads marketing' },
  { label: 'Email Leads', path: '/email-leads', icon: Mail, keywords: 'inbox noreply email' },
  { label: 'Leads Archive', path: '/leads-archive', icon: Globe, keywords: 'archived leads' },
  // Communications
  { label: 'Social Inbox', path: '/social-inbox', icon: MessageSquare, keywords: 'instagram facebook dm' },
  { label: 'Portal Chat', path: '/portal-chat', icon: MessageSquare, keywords: 'customer chat' },
  { label: 'WhatsApp Business', path: '/whatsapp', icon: MessageSquare, keywords: 'whatsapp wa' },
  { label: 'Support Inbox', path: '/support', icon: MessageSquare, keywords: 'tickets help support' },
  // Scheduling
  { label: 'Calendar', path: '/calendar', icon: Calendar, keywords: 'schedule dates diary' },
  { label: 'Smart Schedule', path: '/smart-schedule', icon: Calendar, keywords: 'auto schedule ai planning' },
  { label: 'My Jobs', path: '/my-jobs', icon: Wrench, keywords: 'tasks assignments work' },
  { label: 'Engineers', path: '/engineers', icon: HardHat, keywords: 'team staff installers' },
  { label: 'Needs Linking', path: '/needs-linking', icon: Calendar, keywords: 'orphan calendar events' },
  // Installs
  { label: 'Surveys', path: '/surveys', icon: Globe, keywords: 'site survey list' },
  { label: 'Survey Form', path: '/survey-form', icon: Globe, keywords: 'site survey new form' },
  { label: 'RAMS', path: '/operations/rams', icon: Globe, keywords: 'risk assessment method statement' },
  { label: 'DNO Applications', path: '/dno-applications', icon: Globe, keywords: 'dno ena g99 g98' },
  { label: 'Stock', path: '/stock', icon: Globe, keywords: 'inventory equipment stock' },
  // Contracts
  { label: 'Contract Packs Sent', path: '/admin/contract-packs', icon: Globe, keywords: 'contracts pack history' },
  { label: 'Contract Pack Setup', path: '/admin/contract-setup', icon: Settings, keywords: 'contract template' },
  // Analytics
  { label: 'Reporting', path: '/reporting', icon: BarChart3, keywords: 'reports analytics data' },
  { label: 'Email Analytics', path: '/email-analytics', icon: Mail, keywords: 'email open click tracking' },
  { label: 'Monitoring', path: '/monitoring', icon: BarChart3, keywords: 'health uptime errors monitor' },
  // Tools
  { label: 'Automation', path: '/pipeline-automation', icon: Settings, keywords: 'automation auto actions rules' },
  { label: 'Helpful Links', path: '/helpful-links', icon: Globe, keywords: 'bookmarks links resources' },
  // HR
  { label: 'HR Directory', path: '/hr', icon: Users, keywords: 'hr directory staff' },
  { label: 'My HR Profile', path: '/hr/me', icon: UserCheck, keywords: 'hr profile personal' },
  // Admin
  { label: 'Billing', path: '/admin/billing', icon: Settings, keywords: 'billing payments subscription' },
  { label: 'Time Sheets', path: '/admin/time-sheets', icon: Settings, keywords: 'timesheet hours clock' },
  { label: 'CRM Backups', path: '/admin/backups', icon: Settings, keywords: 'backup restore' },
  { label: 'Access Audit', path: '/admin/access-audit', icon: UserCog, keywords: 'audit access ico compliance' },
  { label: 'GDPR Retention', path: '/admin/gdpr-retention', icon: UserCog, keywords: 'gdpr retention erasure' },
  { label: 'Portal Accounts', path: '/portal-accounts', icon: Globe, keywords: 'customer logins portal accounts' },
  { label: 'Employee Approvals', path: '/employee-approvals', icon: UserCheck, keywords: 'pending staff approvals' },
  { label: 'User Admin', path: '/user-management', icon: UserCog, keywords: 'users roles admin' },
  { label: 'Audit Log', path: '/audit-log', icon: UserCog, keywords: 'audit log history' },
  { label: 'Access Control', path: '/permissions', icon: UserCog, keywords: 'permissions roles access' },
  { label: 'Settings', path: '/settings', icon: Settings, keywords: 'config preferences' },
];

export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [customers, setCustomers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  // Ctrl+K listener
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setQuery('');
      setCustomers([]);
      setSelectedIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Search customers with debounce
  const searchCustomers = useCallback(async (q) => {
    if (q.length < 2) { setCustomers([]); return; }
    setSearching(true);
    try {
      const res = await api.get(`/customers?search=${encodeURIComponent(q)}`);
      setCustomers((res.data || []).slice(0, 5));
    } catch { setCustomers([]); }
    finally { setSearching(false); }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchCustomers(query), 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, searchCustomers]);

  // Filter pages by query
  const filteredPages = query
    ? PAGES.filter(p => (p.label + ' ' + p.keywords).toLowerCase().includes(query.toLowerCase()))
    : PAGES;

  // Build combined results list
  const results = [
    ...customers.map(c => ({ type: 'customer', label: `${c.first_name} ${c.last_name}`, sub: c.email || c.phone || '', path: `/customers/${c.id}`, icon: Users })),
    ...filteredPages.map(p => ({ type: 'page', label: p.label, sub: 'Page', path: p.path, icon: p.icon })),
  ];

  // Reset selected index when results change
  useEffect(() => { setSelectedIdx(0); }, [query]);

  // Keyboard nav
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, results.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && results[selectedIdx]) {
      e.preventDefault();
      navigate(results[selectedIdx].path);
      setOpen(false);
    }
  };

  // Scroll selected into view
  useEffect(() => {
    const el = listRef.current?.children[selectedIdx];
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIdx]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]" data-testid="command-palette">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setOpen(false)} />

      {/* Palette */}
      <div className="relative w-full max-w-xl bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden" data-testid="command-palette-dialog">
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
          <Search className="w-5 h-5 text-slate-400 flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search customers, pages, or actions..."
            className="flex-1 text-sm bg-transparent outline-none placeholder:text-slate-400 text-slate-900"
            data-testid="command-palette-input"
          />
          {searching && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          <kbd className="hidden sm:inline-flex items-center gap-1 text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">
            Esc
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[340px] overflow-y-auto py-2" data-testid="command-palette-results">
          {customers.length > 0 && (
            <div className="px-3 py-1.5">
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Customers</span>
            </div>
          )}
          {results.map((item, idx) => {
            const Icon = item.icon;
            const isCustomerSection = item.type === 'customer';
            const isFirstPage = item.type === 'page' && (idx === 0 || results[idx - 1]?.type === 'customer');

            return (
              <React.Fragment key={`${item.type}-${item.path}`}>
                {isFirstPage && customers.length > 0 && (
                  <div className="px-3 py-1.5 mt-1 border-t border-slate-100">
                    <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Pages</span>
                  </div>
                )}
                {isFirstPage && customers.length === 0 && query.length < 2 && (
                  <div className="px-3 py-1.5">
                    <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Pages</span>
                  </div>
                )}
                <button
                  onClick={() => { navigate(item.path); setOpen(false); }}
                  onMouseEnter={() => setSelectedIdx(idx)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                    idx === selectedIdx ? 'bg-blue-50 text-blue-700' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                  data-testid={`command-result-${idx}`}
                >
                  <Icon className={`w-4 h-4 flex-shrink-0 ${idx === selectedIdx ? 'text-blue-600' : 'text-slate-400'}`} />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium truncate block">{item.label}</span>
                    {item.sub && item.sub !== 'Page' && (
                      <span className="text-xs text-slate-400 truncate block">{item.sub}</span>
                    )}
                  </div>
                  {idx === selectedIdx && <ArrowRight className="w-4 h-4 text-blue-400 flex-shrink-0" />}
                </button>
              </React.Fragment>
            );
          })}
          {results.length === 0 && (
            <div className="py-8 text-center text-sm text-slate-400">
              No results found for "{query}"
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-t border-slate-200 text-xs text-slate-400">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1"><kbd className="bg-white border rounded px-1">↑↓</kbd> Navigate</span>
            <span className="flex items-center gap-1"><kbd className="bg-white border rounded px-1">Enter</kbd> Open</span>
          </div>
          <span className="flex items-center gap-1"><Command className="w-3 h-3" />K to toggle</span>
        </div>
      </div>
    </div>
  );
};
